import { Clock, Mail, MapPin, MessageCircle, Phone, Send } from 'lucide-react';
import { EmptyState, Skeleton } from '../../../components';
import { useLang } from '../../../i18n';
import type { OfficeLocation } from '../../../types';
import { buildMapEmbedUrl } from '../../../utils/mapEmbed';
import './OfficesSection.scss';

export interface OfficesSectionProps {
  offices: OfficeLocation[];
  loading: boolean;
}

function digits(v: string): string {
  return v.replace(/\D/g, '');
}

function tgHref(handle: string): string {
  const v = handle.trim();
  if (/^https?:\/\//i.test(v)) return v;
  return `https://t.me/${v.replace(/^@/, '')}`;
}

function OfficeBlock({ o }: { o: OfficeLocation }) {
  const { t } = useLang();
  return (
    <article className="contacts-offices__item">
      <div className="contacts-offices__info">
        <div className="contacts-offices__head">
          <h3 className="contacts-offices__name">{o.name || o.city}</h3>
          {o.is_primary ? (
            <span className="contacts-offices__primary">{t('contacts.offices.primary_badge')}</span>
          ) : null}
        </div>
        {o.address ? (
          <p className="contacts-offices__row">
            <MapPin size={16} aria-hidden="true" />
            <span>{o.address}</span>
          </p>
        ) : null}
        {o.hours ? (
          <p className="contacts-offices__row">
            <Clock size={16} aria-hidden="true" />
            <span>{o.hours}</span>
          </p>
        ) : null}
        {o.phone ? (
          <p className="contacts-offices__row">
            <Phone size={16} aria-hidden="true" />
            <a href={`tel:${o.phone.replace(/\s/g, '')}`}>{o.phone}</a>
          </p>
        ) : null}
        {o.email ? (
          <p className="contacts-offices__row">
            <Mail size={16} aria-hidden="true" />
            <a href={`mailto:${o.email}`}>{o.email}</a>
          </p>
        ) : null}
        <div className="contacts-offices__actions">
          {o.telegram ? (
            <a
              className="contacts-offices__btn contacts-offices__btn--tg"
              href={tgHref(o.telegram)}
              target="_blank"
              rel="noreferrer noopener"
            >
              <Send size={14} />
              <span>Telegram</span>
            </a>
          ) : null}
          {o.whatsapp ? (
            <a
              className="contacts-offices__btn contacts-offices__btn--wa"
              href={`https://wa.me/${digits(o.whatsapp)}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              <MessageCircle size={14} />
              <span>WhatsApp</span>
            </a>
          ) : null}
        </div>
      </div>
      <div className="contacts-offices__map">
        {(() => {
          const src = buildMapEmbedUrl(o);
          return src ? (
            <iframe
              title={o.name || o.city}
              src={src}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="contacts-offices__map-iframe"
            />
          ) : (
            <div className="contacts-offices__map-fallback">
              <MapPin size={28} aria-hidden="true" />
            </div>
          );
        })()}
      </div>
    </article>
  );
}

export default function OfficesSection({ offices, loading }: OfficesSectionProps) {
  const { t } = useLang();
  const showSkeleton = loading && offices.length === 0;
  const empty = !loading && offices.length === 0;

  return (
    <section className="section section--tight contacts-offices">
      <div className="container">
        <div className="section-header contacts-offices__head-section">
          <span className="eyebrow">{t('contacts.offices.t')}</span>
          <h2 className="contacts-offices__title">{t('contacts.offices.sub')}</h2>
        </div>

        {empty ? (
          <EmptyState title={t('contacts.empty')} />
        ) : showSkeleton ? (
          <div className="contacts-offices__list">
            <Skeleton width="100%" height={300} />
          </div>
        ) : (
          <div className="contacts-offices__list">
            {offices.map((o, i) => (
              <OfficeBlock key={o.id} o={o} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
